import React from 'react';

import { FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';

export type MedcallParticipantActionKey =
  | 'add-medication'
  | 'add-to-my-clients'
  | 'remove-from-my-clients'
  | 'assign-staff'
  | 'assign-nurse'
  | 'assign-psychiatrist'
  | 'participant-status'
  | 'report'
  | 'med-list'
  | 'edit-client'
  | 'client-aliases'
  | 'client-notes'
  | 'medcall-message';

type VisibilityContext = {
  canManageMedications: boolean;
  canManageParticipant: boolean;
  isNurselessProgram: boolean;
  isOnMyClients: boolean;
};

export const getMedcallParticipantActionOrder = ({
  canManageMedications,
  canManageParticipant,
  isNurselessProgram,
  isOnMyClients,
}: VisibilityContext): MedcallParticipantActionKey[] => {
  const actions: MedcallParticipantActionKey[] = [];
  if (canManageMedications) {
    actions.push('add-medication');
  }

  actions.push(isOnMyClients ? 'remove-from-my-clients' : 'add-to-my-clients');
  if (canManageParticipant) {
    actions.push('assign-staff');
  }

  if (!isNurselessProgram) {
    actions.push('assign-nurse');
  }

  if (canManageParticipant) {
    actions.push('assign-psychiatrist', 'participant-status');
  }

  if (!isNurselessProgram) {
    actions.push('report');
  }

  actions.push('med-list');
  if (canManageParticipant) {
    actions.push('edit-client', 'client-aliases');
  }

  actions.push('client-notes', 'medcall-message');
  return actions;
};

type MedcallParticipantActionsProps = VisibilityContext & {
  renderParticipantStatusAction?: (button: React.ReactElement) => React.ReactNode;
  participantActive: boolean;
  pending: boolean;
  onAddMedication: () => void;
  onAddToMyClients: () => void;
  onAssignNurse: () => void;
  onAssignPsychiatrist: () => void;
  onAssignStaff: () => void;
  onClientAliases: () => void;
  onClientNotes: () => void;
  onEditClient: () => void;
  onMedcallMessage: () => void;
  onMedicationList: () => void;
  onParticipantStatus: () => void;
  onRemoveFromMyClients: () => void;
  onReport: () => void;
};

const ACTION_PRESENTATION: Record<MedcallParticipantActionKey, { label: string }> = {
  'add-medication': {
    label: 'Add Medication',
  },
  'add-to-my-clients': {
    label: 'Add to My Clients',
  },
  'remove-from-my-clients': {
    label: 'Remove From My Clients',
  },
  'assign-staff': {
    label: 'Assign Staff',
  },
  'assign-nurse': {
    label: 'Assign Nurse',
  },
  'assign-psychiatrist': {
    label: 'Assign Psychiatrist',
  },
  'participant-status': {
    label: 'Participant Status',
  },
  report: {
    label: 'Report',
  },
  'med-list': {
    label: 'Med List',
  },
  'edit-client': {
    label: 'Edit Client',
  },
  'client-aliases': {
    label: 'Client Aliases',
  },
  'client-notes': {
    label: 'Client Notes',
  },
  'medcall-message': {
    label: 'MedCall Message',
  },
};

export const MedcallParticipantActions = ({
  canManageMedications,
  canManageParticipant,
  isNurselessProgram,
  isOnMyClients,
  onAddMedication,
  onAddToMyClients,
  onAssignNurse,
  onAssignPsychiatrist,
  onAssignStaff,
  onClientAliases,
  onClientNotes,
  onEditClient,
  onMedcallMessage,
  onMedicationList,
  onParticipantStatus,
  onRemoveFromMyClients,
  onReport,
  participantActive,
  pending,
  renderParticipantStatusAction,
}: MedcallParticipantActionsProps) => {
  const actions = !canManageParticipant ? ['med-list'] as MedcallParticipantActionKey[] : getMedcallParticipantActionOrder({
    canManageMedications,
    canManageParticipant,
    isNurselessProgram,
    isOnMyClients,
  });
  const handlers: Record<MedcallParticipantActionKey, () => void> = {
    'add-medication': onAddMedication,
    'add-to-my-clients': onAddToMyClients,
    'remove-from-my-clients': onRemoveFromMyClients,
    'assign-staff': onAssignStaff,
    'assign-nurse': onAssignNurse,
    'assign-psychiatrist': onAssignPsychiatrist,
    'participant-status': onParticipantStatus,
    report: onReport,
    'med-list': onMedicationList,
    'edit-client': onEditClient,
    'client-aliases': onClientAliases,
    'client-notes': onClientNotes,
    'medcall-message': onMedcallMessage,
  };

  return (
    <div className="flex flex-wrap gap-2" data-testid="medcall-participant-actions">
      {actions.map((action) => {
        const presentation = ACTION_PRESENTATION[action];
        const label = action === 'participant-status'
          ? (participantActive ? 'Deactivate Client' : 'Activate Client')
          : presentation.label;
        const button = (
          <Button
            key={action}
            className="h-8 gap-2"
            id={`medcall-workspace-participant-${action}`}
            data-medcall-action={action}
            disabled={pending}
            onClick={handlers[action]}
            size="sm"
            type="button"
            variant="outline"
          >
            <FileText className="h-4 w-4" />{label}
          </Button>
        );
        if (action === 'participant-status' && renderParticipantStatusAction) {
          return <React.Fragment key={action}>{renderParticipantStatusAction(button)}</React.Fragment>;
        }
        return button;
      })}
    </div>
  );
};
